<template>
  <fieldset :disabled="hasMessengerInstalledApp()">
    <div class="field" v-if="!hasMessengerInstalledApp()">
      <label class="label">Log in to Facebook to authenticate your account</label>
      <div class="control">
        <a class="button is-link" v-if="!connected" @click="login()" :class="{ 'is-loading': loginInProgress }"
          >Authenticate with Facebook</a
        >
        <a class="button is-success" v-if="connected" :class="{ 'is-loading': loginInProgress }">Authenticated</a>
        <!--<a class="button is-link" v-if="connected" @click="logout()" :class="{ 'is-loading': loginInProgress }">Sign out of Facebook</a>-->
      </div>
    </div>

    <div class="field" v-if="!hasMessengerInstalledApp()">
      <label class="label">Facebook Page</label>
      <div class="control">
        <div class="select is-fullwidth" :class="{ 'is-loading': loadingPages }">
          <select v-model="selectedPage" :disabled="!connected || loadingPages">
            <option v-for="page in userPages" :key="page.id" :value="page">{{ page.name }}</option>
          </select>
        </div>
        <p class="help is-danger" v-if="$v.selectedPage.$invalid && $v.selectedPage.$dirty">
          The Facebook Page is required
        </p>
      </div>
    </div>

    <div class="field" v-if="hasMessengerInstalledApp()">
      <label class="label">Facebook Page</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select>
            <option>{{ messengerApp?.pageName }}</option>
          </select>
        </div>
      </div>
    </div>
    <n-button-group>
      <n-button variant="primary" @click="save()" :disabled="$v.$invalid" :loading="actionInProgress">Submit</n-button>
      <n-button @click="cancel()" variant="primary--outline">Cancel</n-button>
    </n-button-group>
  </fieldset>
</template>

<script lang="ts" setup>
import { required } from '@vuelidate/validators';
import { FacebookApi } from './services/facebookAppApi';
import { messengerApi } from './services/messengerApi';
import { instagramApi } from './services/instagramApi';
import Toast from '@/shared/services/toast';
import { fbGetSdk, fbLogin, fbLogout, fbGetLoginStatus } from './services/messengerSdkWrapper';
import { FacebookPage, MessengerInstalledApp, InstallMessengerRequest } from './models';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { MessengerGraphApi } from '@/features/apps/messenger/models/messengerInstalledApp';

interface LoginScope {
  scope: string;
}

interface LoginOptions {
  messenger: LoginScope;
  instagram: LoginScope;
}

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);

const selectedPage = ref<FacebookPage | null>(null);
const userPages = ref<FacebookPage[]>([]);
const messengerApp = ref<MessengerInstalledApp | null>(null);
const loginInProgress = ref(false);
const actionInProgress = ref(false);
const loadingPages = ref(false);
const connected = ref(false);

const validations = {
  selectedPage: { required },
};

const $v = useVuelidate(validations, { selectedPage });

const emit = defineEmits<{
  (e: 'loaded'): void;
  (e: 'configSaved'): void;
}>();

const loginOptions: LoginOptions = {
  messenger: {
    scope: 'business_management,pages_show_list,pages_read_engagement,pages_messaging,pages_manage_metadata',
  },
  instagram: {
    scope:
      'business_management,pages_show_list,pages_read_engagement,pages_manage_metadata,instagram_basic,instagram_manage_messages',
  },
};

let api: FacebookApi;

onBeforeMount(() => {
  api = currentApp.value.id === 'instagram' ? instagramApi : messengerApi;
  if (currentApp.value.isInstalled) {
    api
      .getInstalledApp(currentWorkspace.value.id)
      .then((fetchedApp: MessengerInstalledApp) => {
        loadFacebookLoginSdk(currentApp.value.id, fetchedApp.messengerGraphApi);

        if (fetchedApp.installed) {
          messengerApp.value = fetchedApp;
          selectedPage.value = {
            access_token: '',
            id: fetchedApp.pageId,
            name: fetchedApp.pageName,
          };
        }
      })
      .catch((err) => {
        // if it's not found, then we haven't installed the messenger app fully, so let them fill in the form
        if (err.status !== 404) {
          // show error state...
        }
      })
      .finally(() => {
        emit('loaded');
      });
  } else {
    emit('loaded');
  }
});

onBeforeUnmount(() => {
  if (!connected.value) {
    return;
  }
  fbLogout();
});

function loadFacebookLoginSdk(currentApp: string, apiDetails: MessengerGraphApi) {
  const appId = currentApp === 'instagram' ? apiDetails.instagramAppId : apiDetails.messengerAppId;
  fbGetSdk(appId, apiDetails.version).then(() => {
    fbGetLoginStatus().then((res: any) => {
      if (res.status === 'connected') {
        onConnected(res.authResponse.accessToken);
      }
    });
  });
}

function hasMessengerInstalledApp() {
  return messengerApp.value !== null;
}

function logout() {
  if (!connected.value) {
    return;
  }

  fbLogout().then(() => {
    fbGetLoginStatus().then((res: any) => {
      if (res.status !== 'connected') {
        connected.value = false;
      }
    });
  });
}

function login() {
  loginInProgress.value = true;
  const loginScope = loginOptions[currentApp.value.id as keyof LoginOptions].scope;
  fbLogin(loginScope)
    .then((res: any) => {
      if (res.status === 'connected') {
        onConnected(res.authResponse.accessToken);
      }
    })
    .finally(() => {
      loginInProgress.value = false;
    });
}

function onConnected(authToken: string) {
  connected.value = true;
  getFacebookPages(authToken);
}

function getFacebookPages(authToken: string) {
  loadingPages.value = true;
  api
    .getUserPages(authToken)
    .then((pages: FacebookPage[]) => {
      userPages.value = pages;
    })
    .finally(() => (loadingPages.value = false));
}

function save() {
  if (actionInProgress.value) {
    return;
  }

  if (!selectedPage.value) {
    return;
  }

  actionInProgress.value = true;

  const request = {
    installId: currentApp.value.installedAppId,
    workspaceId: currentWorkspace.value.id,
    pageId: selectedPage.value.id,
    pageName: selectedPage.value.name,
    pageAccessToken: selectedPage.value.access_token,
  } as InstallMessengerRequest;

  api
    .install(request)
    .then(() => emit('configSaved'))
    .catch((err) => Toast.showError(err.message))
    .finally(() => (actionInProgress.value = false));
}

function cancel() {
  router.push({ name: 'apps' });
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.action-group {
  margin-top: 50px;
  margin-bottom: 20px;
}
</style>
